import { Clickable, Icon, Row } from 'components';
import { imageResizer, useOutsideClick } from 'core';
import Picker from 'emoji-picker-react';
import { theme } from 'lib';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  width: 100%;
  min-height: ${(props) => (props.focused ? '42px' : '43px')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  border: ${(props) => (props.focused ? '2px' : '1px')};
  background-color: ${theme.GREY_10};
  border-radius: 50px;
  font-size: 16px;
  height: 40px;
  border: 0px;

  ${(props) =>
    props.multiline &&
    `
    border-radius: 16px;
    ${props.numberOfLines && `max-height: ${(props.isFocused ? 42 : 43) * props.numberOfLines}px;`}
  `};

  ${(props) =>
    props.ht &&
    `
    height: ${props.ht};
    align-items: flex-start;
  `}
`;

const StyledInput = styled.input`
  font-family: CeraPro;
  flex: 1;
  width: 100%;
  font-size: 16px;
  ${(props) => props.center && `text-align: center;`};

  ${(props) =>
    props.multiline &&
    `
    padding: 10px 0;
  `};
`;

const ChatInput = (props) => {
  const [showEmojis, setShowEmojis] = useState(false),
    hasBody = props.value?.length,
    emojiRef = useRef(null),
    fileInput = useRef(null),
    defaultInputRef = useRef(null),
    inputRef = props.inputRef || defaultInputRef;

  useOutsideClick(emojiRef, () => showEmojis && setShowEmojis(false), [
    inputRef.current?.className,
    'emoji-button'
  ]);

  function handleSend() {
    setShowEmojis(false);
    props.send();
  }

  function openFilePicker() {
    fileInput.current.click();
  }

  function handleEmoji(emoji) {
    const ref = inputRef?.current;

    if (!ref) return console.log('ref is required');

    /*
      Read the live DOM value/selection instead of props.value — the emoji
      picker memoizes its config on mount, so this callback can be a stale
      closure holding text the user has since edited or cleared. The DOM
      node always reflects the current controlled value.
    */
    const liveValue = ref.value || '';
    const start = ref.selectionStart ?? liveValue.length;
    const end = ref.selectionEnd ?? start;

    props.onChange({
      target: {
        value: liveValue.substring(0, start) + emoji + liveValue.substring(end)
      }
    });
  }

  async function handleFile(e) {
    // get file info
    const file = e?.target?.files[0];
    if (!file) return;

    const reader = new FileReader();

    // read file
    const image = await imageResizer({
      height: 1440,
      width: 1440,
      format: 'jpeg',
      file,
      output: 'blob',
      quality: 80
    });

    props.handleImage(image);
  }

  return (
    <React.Fragment>
      <Container {...props}>
        <Row center wid="100%" pad="0 16px">
          <div ref={emojiRef}>
            {showEmojis && (
              <Row bottom="60px" pos="absolute">
                <Picker onEmojiClick={({ emoji }) => handleEmoji(emoji)} />
              </Row>
            )}

            <Clickable onClick={() => setShowEmojis(!showEmojis)}>
              <Icon type="emoji" dimensions={22} col={theme.GREY_50} marg="0 8px 0 0" />
            </Clickable>
          </div>

          <StyledInput
            {...props}
            ref={inputRef}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && hasBody) {
                handleSend();
              }
            }}
            placeholderTextColor={theme.GREY_60}
            placeholderStyle={{ fontSize: 16 }}
          />

          <Clickable onClick={(hasBody ? handleSend : openFilePicker) || (() => null)}>
            <Icon
              type={hasBody ? 'send' : 'attach'}
              dimensions={22}
              col={hasBody ? theme.TALE : theme.GREY_50}
              marg="0 0 0 8px"
            />

            <input
              type="file"
              ref={fileInput}
              style={{ width: 0, height: 0, opacity: 0, position: 'absolute' }}
              onChange={handleFile}
            />
          </Clickable>
        </Row>
      </Container>
    </React.Fragment>
  );
};

export default ChatInput;
